import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { LoaderCircle, Mail, MailCheck, Send } from 'lucide-react';
import { useAuth } from '../../context/AuthProvider.jsx';
import { useNotification } from '../../components/Notifications/NotificationContext';
import PushTip from '../../components/UI/PushTip/PushTip';
import '../../components/PushNotifications/PushNotifications.css';
import AuthShell from './AuthShell.jsx';

const RESEND_COOLDOWN = 60; // segundos

export default function ConfirmEmail() {
  const location = useLocation();
  const { resendVerification } = useAuth();
  const { showError, showSuccess } = useNotification();

  // Email vem do redirect pós-cadastro (state) ou da query (?email=)
  const stateEmail =
    location.state?.email ||
    new URLSearchParams(location.search).get('email') ||
    '';

  const [email, setEmail] = useState(stateEmail);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleResend = async (e) => {
    e?.preventDefault();
    if (!email || resending || cooldown > 0) return;
    setResending(true);
    try {
      await resendVerification(email);
      showSuccess('Link reenviado! Confira sua caixa de entrada e o spam.');
      setCooldown(RESEND_COOLDOWN);
    } catch (err) {
      showError('Não foi possível reenviar agora. Tente novamente em alguns minutos.');
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthShell
      active="register"
      title={
        <>
          Falta <span className="sp-gradient-text">um passo.</span>
        </>
      }
    >
      <div className="sp-verify-sent">
        <div className="sp-verify-icon">
          <MailCheck size={30} strokeWidth={2} />
        </div>

        <h2 className="sp-card-title">Confirme seu email</h2>
        <p className="sp-card-sub">
          {stateEmail ? (
            <>
              Enviamos um link de confirmação para{' '}
              <strong>{stateEmail}</strong>.
              Abra o email e clique em <strong>Confirmar meu email</strong>{' '}
              para ativar sua conta e entrar.
            </>
          ) : (
            <>
              Sua conta precisa de confirmação de email. Informe seu email abaixo
              para reenviarmos o link de ativação.
            </>
          )}
        </p>

        {/* Aviso de SPAM no padrão de tips da plataforma */}
        <PushTip
          className="sp-confirm-tip"
          strong="Não achou o email?"
        >
          Verifique a caixa de <strong>SPAM / Lixo eletrônico</strong> — é comum
          ele cair lá. Se estiver no spam, marque como &quot;Não é spam&quot;
          para os próximos chegarem certinho.
        </PushTip>

        <form onSubmit={handleResend}>
          {!stateEmail && (
            <div className="sp-field" style={{ textAlign: 'left' }}>
              <label htmlFor="email" className="sp-label">Email da sua conta</label>
              <div className="sp-input-wrapper">
                <Mail className="sp-input-icon" size={16} aria-hidden="true" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  className="sp-input"
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            className="sp-btn-primary"
            disabled={resending || cooldown > 0 || !email}
            style={{ marginTop: '1rem' }}
          >
            {resending ? (
              <>
                <LoaderCircle className="sp-spin" size={18} aria-hidden="true" />
                Reenviando...
              </>
            ) : cooldown > 0 ? (
              <>Reenviar novamente em {cooldown}s</>
            ) : (
              <>
                Reenviar email de confirmação
                <Send className="sp-btn-arrow" size={17} aria-hidden="true" />
              </>
            )}
          </button>
        </form>
      </div>

      <div className="sp-card-footer">
        Já confirmou?{' '}
        <Link to="/login" className="sp-link">
          Fazer login
        </Link>
      </div>
    </AuthShell>
  );
}
